import { StringOfMax15Chars, StringOfMax2000CharsAndNoPositionalVariable, StringOfMax2000CharsAndSinglePositionalVariable, StringOfMax20Chars, StringOfMax25Chars } from "./strings";

// #region Button
export type MetaTemplateButtons = { 
    type: "BUTTONS",
    buttons: MetaTemplateButton[]
};

export type MetaTemplateButton = MetaCopyCodeButton | MetaMultiProductButton | MetaSingleProductButton | MetaOneTimePasswordButton | MetaVoiceCallButton | MetaPhoneNumberButton | MetaQuickReplyButton | MetaURLButton

/** Maximum: 1 MetaCopyCodeButton per template */
export type MetaCopyCodeButton = {
    type: "COPY_CODE",
    example: StringOfMax15Chars
}


/** SCAFFOLD — depende do portfólio de negócios (catálogo). [doc](https://developers.facebook.com/documentation/business-messaging/whatsapp/catalogs/mpm-template-messages) */
export type MetaMultiProductButton = {
    type: "MPM",
    text: StringOfMax25Chars
}

/** SCAFFOLD — depende do portfólio de negócios (catálogo). [doc1](https://developers.facebook.com/documentation/business-messaging/whatsapp/catalogs/spm-template-messages) [doc2](https://developers.facebook.com/documentation/business-messaging/whatsapp/catalogs/product-card-carousel-template-messages) */
export type MetaSingleProductButton = {
    type: "SPM",
    text: StringOfMax25Chars
}

/** SCAFFOLD — botão OTP para templates de Autenticação (config extra). [doc](https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/authentication-templates/authentication-templates) */
export type MetaOneTimePasswordButton = {
    type: "OTP",
    otp_type: "one-tap" | "copy-code" | "zero-tap",
    text?: StringOfMax25Chars,
    /** Necessário para one-tap/zero-tap — vincula apps (config extra). */
    supported_apps?: { package_name: string, signature_hash: string }[]
}

/** SCAFFOLD — botão de ligação (config extra). [doc](https://developers.facebook.com/documentation/business-messaging/whatsapp/calling/call-button-messages-deep-links/#create-and-send-whatsapp-call-button-template-message) */
export type MetaVoiceCallButton = {
    type: "VOICE_CALL",
    text: StringOfMax25Chars
}

export type MetaPhoneNumberButton = {
    type: "PHONE_NUMBER",
    text: StringOfMax20Chars,
    /** É o texto que aparece na label do botão (usualmente 'ligar' ou algo do tipo) */
    phone_number: StringOfMax25Chars
}

/** Max: 10 per template, and all QuickReply and Non-QuickReply buttons must be grouped together (close to each other) in the buttons array */
export type MetaQuickReplyButton = {
    type: "QUICK_REPLY",
    text: StringOfMax25Chars
}

/** Max: 2 per template */
export type MetaURLButton = MetaVariablelessURLButton | MetaURLButtonWithVariables

export type MetaVariablelessURLButton = {
    type: "URL",
    text: StringOfMax25Chars,
    url: StringOfMax2000CharsAndNoPositionalVariable,
}
export type MetaURLButtonWithVariables = {
    type: "URL",
    text: StringOfMax25Chars,
    url: StringOfMax2000CharsAndSinglePositionalVariable,

    /** Required if `url` contains a variable */
    example: [
        StringOfMax2000CharsAndSinglePositionalVariable
    ]
}

// #endregion Button

// #region Button scaffolding (variants + gating)

/**
 * Nível de liberação de cada variante de botão:
 * - `ready`     : pronto para uso.
 * - `advanced`  : botões básicos ainda não liberados (gate META_TEMPLATE_DEBUG).
 * - `portfolio` : depende do portfólio de negócios/catálogo (gate portfolio).
 * - `authApps`  : OTP one-tap/zero-tap com apps (gate auth apps).
 */
export type MetaButtonGate = 'ready' | 'advanced' | 'portfolio' | 'authApps';

export type MetaButtonKind =
    | 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER' | 'COPY_CODE'
    | 'OTP' | 'VOICE_CALL' | 'MPM' | 'SPM';

export interface MetaButtonVariantDescriptor {
    kind : MetaButtonKind;
    /** Rótulo PT-BR para a UI. */
    label: string;
    icon : string;
    gate : MetaButtonGate;
}

/** Catálogo de variantes de botões — dirige o que a UI oferece por gate. */
export const MetaButtonVariants: MetaButtonVariantDescriptor[] = [
    { kind: 'QUICK_REPLY' , label: 'Resposta rápida'   , icon: 'ri-chat-1-line'        , gate: 'ready'     },
    { kind: 'URL'         , label: 'Link (URL)'         , icon: 'ri-external-link-line' , gate: 'ready'     },
    { kind: 'PHONE_NUMBER', label: 'Ligar'              , icon: 'ri-phone-line'         , gate: 'ready'     },
    { kind: 'COPY_CODE'   , label: 'Copiar código'      , icon: 'ri-file-copy-line'     , gate: 'ready'     },
    { kind: 'OTP'         , label: 'Código OTP'         , icon: 'ri-shield-keyhole-line', gate: 'authApps'  },
    { kind: 'VOICE_CALL'  , label: 'Chamada de voz'     , icon: 'ri-phone-fill'         , gate: 'authApps'  },
    { kind: 'MPM'         , label: 'Múltiplos produtos' , icon: 'ri-store-2-line'       , gate: 'portfolio' },
    { kind: 'SPM'         , label: 'Produto único'      , icon: 'ri-price-tag-3-line'   , gate: 'portfolio' },
];

/** Cria um botão vazio da variante informada (para o editor de botões). */
export function makeEmptyButton(kind: MetaButtonKind): MetaTemplateButton {
    switch (kind) {
        case 'QUICK_REPLY' : return { type: 'QUICK_REPLY' , text: '' as StringOfMax25Chars };
        case 'URL'         : return { type: 'URL'         , text: '' as StringOfMax25Chars, url: '' as StringOfMax2000CharsAndNoPositionalVariable };
        case 'PHONE_NUMBER': return { type: 'PHONE_NUMBER', text: '' as StringOfMax20Chars, phone_number: '' as StringOfMax25Chars };
        case 'COPY_CODE'   : return { type: 'COPY_CODE'   , example: '' as StringOfMax15Chars };
        case 'OTP'         : return { type: 'OTP'         , otp_type: 'copy-code' };
        case 'VOICE_CALL'  : return { type: 'VOICE_CALL'  , text: '' as StringOfMax25Chars };
        case 'MPM'         : return { type: 'MPM'         , text: '' as StringOfMax25Chars };
        case 'SPM'         : return { type: 'SPM'         , text: '' as StringOfMax25Chars };
    }
}

/** Empacota botões no formato de componente aceito pela Meta. */
export function makeButtonsComponent(buttons: MetaTemplateButton[]): MetaTemplateButtons {
    return { type: 'BUTTONS', buttons };
}

/**
 * Operação inversa de `makeButtonsComponent`: relê o `buttonsJson` gravado para dentro do
 * editor. Sem isto, editar um template com botões abria o editor vazio e o salvamento
 * mandava a lista de componentes SEM eles — e a Meta substitui a lista inteira, então os
 * botões seriam perdidos no template real.
 *
 * O `type` volta em maiúsculo porque é assim que as variantes são identificadas na UI (a
 * Meta aceita/devolve as duas grafias).
 */
export function parseButtonsComponent(json?: string | null): MetaTemplateButton[] {
    if (!json) return [];
    try {
        const parsed = JSON.parse(json) as { buttons?: unknown } | null;
        if (!Array.isArray(parsed?.buttons)) return [];

        const kinds = MetaButtonVariants.map(v => v.kind as string);
        return (parsed.buttons as unknown[])
            .filter((b): b is Record<string, unknown> => !!b && typeof b === 'object')
            .map(b => ({ ...b, type: String(b.type ?? '').toUpperCase() }))
            .filter(b => kinds.includes(b.type)) as MetaTemplateButton[];
    } catch {
        // JSON ilegível não derruba a edição: o editor abre sem botões.
        return [];
    }
}

// #endregion Button scaffolding

// Validação de combinação/composição vive em `meta-template-rules.ts`
// (fonte única das regras da Meta).